import { useQuery } from '@apollo/client';

import { useAccountCompanyId } from 'application/hooks';
import { ModalTypes, useModalContext } from 'application/providers/ModalProvider';
import { toast } from '@shared/ui';

import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import { AcceptanceAdvanceStatus, AuthCurrentUserDocument, AuthCurrentUserQuery } from '@shared/api/graphql/generated';
import { useAcceptanceAdvancedContext, SBBOL_REDIRECT_KEY } from '@entities/billing';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import { useCurrentUserSberConnection } from '@entities/auth';

import { TEXTS } from './texts';
import { useStartAcceptanceAdvancedSigning } from './useStartAcceptanceAdvancedSigning';

interface UseSberBusinessOnlineActions {
  acceptanceAdvancedStatus: AcceptanceAdvanceStatus | null;
  payerAccount: string;
  startSigningAcceptanceAdvancedLoading: boolean;
  acceptanceAdvancedLoading: boolean;
  isCompanyLoading: boolean;
  hasSberConnection: boolean;
  onStartSigningAcceptanceAdvanced: () => Promise<void>;
}

const useSberBusinessOnlineActions = (): UseSberBusinessOnlineActions => {
  const { openModal } = useModalContext();
  const { isIOS, isSafariDesktop } = useMedia();
  const isIos = isIOS || isSafariDesktop;

  const companyIdFromStore = useAccountCompanyId();

  const { data: userData } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const companyId = companyIdFromStore || userData?.currentUserPureQuery?.mainCompany?.id || '';

  const { acceptanceAdvanced, loading: acceptanceAdvancedLoading } = useAcceptanceAdvancedContext(companyId);

  const hasSberConnection = useCurrentUserSberConnection();

  const { startSigningAcceptanceAdvancedLoading, startSigningAcceptanceAdvanced } = useStartAcceptanceAdvancedSigning();

  const onStartSigningAcceptanceAdvanced = async () => {
    localStorage.setItem(SBBOL_REDIRECT_KEY, 'true');

    startSigningAcceptanceAdvanced(companyId);

    if (!isIos) {
      toast.info(TEXTS.SBER_BUSINESS_ONLINE_ACCEPTANCE_ADVANCED_CREATED, { duration: 10000 });
    }
  };

  const handleStartSigningWithWarning = async () => {
    openModal(ModalTypes.SBER_REDIRECT_WARNING, { onConfirm: onStartSigningAcceptanceAdvanced });
  };

  return {
    acceptanceAdvancedStatus: acceptanceAdvanced?.status || null,
    payerAccount: acceptanceAdvanced?.payerAccount || '',
    startSigningAcceptanceAdvancedLoading,
    acceptanceAdvancedLoading,
    isCompanyLoading: !companyId,
    hasSberConnection,
    onStartSigningAcceptanceAdvanced: handleStartSigningWithWarning,
  };
};

export default useSberBusinessOnlineActions;
